import React, { useEffect, useRef } from 'react';
import { Space, Tooltip } from 'antd';
import {
  BoldOutlined, ItalicOutlined, UnderlineOutlined, UnorderedListOutlined,
} from '@ant-design/icons';
import { useI18n } from '../i18n';
import { brandSemantic } from '../config/brand';

const TOOLBAR_ACTIONS = [
  { command: 'bold', icon: <BoldOutlined />, label: 'Bold' },
  { command: 'italic', icon: <ItalicOutlined />, label: 'Italic' },
  { command: 'underline', icon: <UnderlineOutlined />, label: 'Underline' },
  { command: 'insertUnorderedList', icon: <UnorderedListOutlined />, label: 'Bulleted list' },
];

// Lightweight rich-text editor: a styled contentEditable surface with a small
// formatting toolbar. Browsers natively convert pasted clipboard images
// (screenshots, copied HTML with embedded <img> tags) into inline content
// when pasted into a contentEditable element, so no custom paste handling is needed.
export default function RichTextEditor({
  value = '',
  onChange,
  placeholder = '',
  minHeight = 90,
}) {
  const { t } = useI18n();
  const editorRef = useRef(null);
  const isFocused = useRef(false);

  useEffect(() => {
    const node = editorRef.current;
    if (!node) return;
    // Avoid resetting innerHTML (and the caret) while the user is actively typing.
    if (isFocused.current) return;
    if (node.innerHTML !== (value || '')) {
      node.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    onChange?.(editorRef.current?.innerHTML || '');
  };

  const runCommand = (command) => {
    editorRef.current?.focus();
    document.execCommand(command);
    handleInput();
  };

  const isEmpty = !value || value === '<br>' || value === '<p></p>';

  return (
    <div
      style={{
        border: `1px solid ${brandSemantic.border}`,
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      <Space
        size={2}
        style={{
          padding: '4px 6px',
          borderBottom: `1px solid ${brandSemantic.borderSecondary}`,
          background: brandSemantic.layout,
        }}
      >
        {TOOLBAR_ACTIONS.map((action) => (
          <Tooltip key={action.command} title={t(action.label)}>
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); runCommand(action.command); }}
              style={{
                width: 28,
                height: 28,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                background: 'transparent',
                borderRadius: 4,
                cursor: 'pointer',
                color: brandSemantic.textSecondary,
              }}
            >
              {action.icon}
            </button>
          </Tooltip>
        ))}
      </Space>
      <div style={{ position: 'relative' }}>
        {isEmpty && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              left: 11,
              color: brandSemantic.textTertiary,
              pointerEvents: 'none',
            }}
          >
            {placeholder}
          </div>
        )}
        <div
          ref={editorRef}
          className="rich-text-editor-content"
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onFocus={() => { isFocused.current = true; }}
          onBlur={() => { isFocused.current = false; handleInput(); }}
          style={{
            minHeight,
            padding: '8px 11px',
            outline: 'none',
            fontSize: 14,
            lineHeight: 1.5,
            color: brandSemantic.text,
            overflowY: 'auto',
          }}
        />
      </div>
    </div>
  );
}
